import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { Search, Bell, X, Briefcase, UserPlus, CheckCircle } from "lucide-react"; 
import api from "../../services/api"; 

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  let adminData = {};
  try {
    const rawData = localStorage.getItem("admin");
    if (rawData && rawData !== "undefined") {
      adminData = JSON.parse(rawData);
    }
  } catch (error) {
    console.error("Admin data corrupt, resetting:", error);
    adminData = {};
  }

  const adminName = adminData?.name || "Super Admin";
  const adminRole = adminData?.role === 'admin' ? 'Administrator' : 'HR Manager';

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  
  // 🌟 Audio se related saari linein permanently delete kar di hain
  const prevCountRef = useRef(0);

  useEffect(() => {
    const fetchNotifications = async () => {
      const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await api.get("/admin/notifications", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data && Array.isArray(res.data.notifications)) {
          setNotifications(res.data.notifications);
          const newUnreadCount = res.data.unread_count || 0;

          // Sound play karne wala logic bhi hata diya hai
          prevCountRef.current = newUnreadCount;
          setUnreadCount(newUnreadCount);
        }
      } catch (error) {
        console.error("API Error:", error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000); 
    return () => clearInterval(interval);
  }, []);

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem("admin_token") || localStorage.getItem("token");
      if(!token) return;

      await api.post("/admin/notifications/mark-read", {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUnreadCount(0);
      prevCountRef.current = 0; 
    } catch (error) {
      console.error("Error marking read", error);
    }
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#F8F9FA]">
      <Sidebar />

      <div className="flex-1 flex flex-col h-screen overflow-hidden min-w-0 relative">
        <header className="bg-white border-b border-gray-100 px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between shrink-0 relative z-[40]">
          <div className="truncate pr-2">
            <h2 className="text-base sm:text-xl font-bold text-gray-800 flex items-center gap-1 sm:gap-2 truncate">
              Welcome Back <span className="hidden sm:inline">👋</span>
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 hidden md:block">{today}</p>
          </div>

          <div className="flex items-center gap-3 sm:gap-5 shrink-0">
            <div className="relative hidden lg:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="text" 
                placeholder="Search..." 
                className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-yellow-400 focus:bg-white w-48 xl:w-64 transition-all"
              />
            </div>

            <button 
              onClick={(e) => {
                e.preventDefault();
                setShowDropdown(true);
                if (unreadCount > 0) markAllAsRead();
              }}
              className="relative p-2 text-gray-500 hover:text-yellow-500 transition-colors bg-gray-50 sm:bg-transparent rounded-full"
            >
              <Bell size={18} className={showDropdown ? "text-[#FED547] sm:w-5 sm:h-5" : "sm:w-5 sm:h-5"} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 sm:top-0 sm:right-0 h-4 w-4 sm:h-5 sm:w-5 bg-red-500 text-white text-[9px] sm:text-[10px] font-bold rounded-full border-2 border-white flex items-center justify-center shadow-sm animate-bounce">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <div className="flex items-center gap-2 sm:gap-3 sm:border-l sm:border-gray-200 sm:pl-5 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-400 flex items-center justify-center text-black font-bold text-sm sm:text-lg shadow-sm shrink-0">
                {adminName.charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-bold text-gray-800 truncate max-w-[100px] lg:max-w-[150px]">{adminName}</p>
                <p className="text-[10px] sm:text-xs text-gray-500 truncate">{adminRole}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto p-3 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {showDropdown && createPortal(
        <div 
          className="fixed inset-0 flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ zIndex: 99999999, backgroundColor: 'rgba(0,0,0,0.6)' }} 
          onClick={() => setShowDropdown(false)}
        >
          <div 
            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden flex flex-col relative transform animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()} 
          >
            <div className="flex items-center justify-between px-5 py-4 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-extrabold text-gray-900">Notifications</h3>
                <span className="text-[10px] font-bold px-2.5 py-1 bg-[#FED547] text-black rounded-full shadow-sm uppercase tracking-wider">
                  {notifications?.length || 0} Total
                </span>
              </div>
              <button 
                onClick={() => setShowDropdown(false)}
                className="text-gray-400 hover:text-red-500 bg-white hover:bg-red-50 w-8 h-8 flex items-center justify-center rounded-full transition-colors shadow-sm border border-gray-100"
              >
                <X size={18} strokeWidth={3} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto bg-white p-3">
              {notifications && notifications.length > 0 ? (
                notifications.map((item, index) => (
                  <div 
                    key={index} 
                    onClick={() => {
                      setShowDropdown(false);
                      // 🌟 Specific Employer Profile Page par redirect
                      if (item.employer_id) {
                        navigate(`/admin/employers/${item.employer_id}`);
                      } else {
                        navigate('/admin/employers');
                      }
                    }}
                    className="p-4 mb-2 border border-gray-50 rounded-xl hover:bg-yellow-50 hover:border-yellow-200 transition-all flex items-start gap-4 cursor-pointer"
                  >
                    <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center shrink-0 text-yellow-600 shadow-sm border border-yellow-200">
                      {item.type === 'employer' || item.type === 'employer_register' ? <Briefcase className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{item.title}</p>
                      <p className="text-xs text-gray-600 mt-1 leading-relaxed">{item.message}</p>
                      <span className="text-[10px] text-gray-400 mt-2 block font-semibold uppercase tracking-wider">{item.time}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-12 text-center text-sm text-gray-500 flex flex-col items-center justify-center">
                  <CheckCircle className="w-14 h-14 text-emerald-400 mb-4 opacity-50" />
                  <p className="font-extrabold text-gray-800 text-lg">All caught up!</p>
                  <p className="text-xs text-gray-400 mt-1">You have no new notifications right now.</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}